// One-shot effects (a flare, a ring, a mote) whose only cleanup is their tween's `onComplete`.
//
// The encounter kills every tween when a new board arrives and when a playback is skipped. In the
// Phaser this client runs, `tweens.killAll()` destroys each tween outright: neither `onComplete` nor
// `onStop` runs, so an effect waiting on its tween to remove it would hang frozen on the board.
// Each such effect is registered here with `oneShot`, and `killTweens` (used in place of a bare
// `killAll`) destroys every one still up along with the tweens.
//
// Loops are a different case: drive them from the scene's update (see ExploreLayer, ObjectiveGlow)
// rather than a `repeat: -1` tween, which the same `killAll` stops for good.
import Phaser from 'phaser';

const live = new WeakMap<Phaser.Scene, Set<Phaser.GameObjects.GameObject>>();

/** Registers `o`, removed only by a tween, to go when the scene's tweens are killed; returns it. */
export function oneShot<T extends Phaser.GameObjects.GameObject>(scene: Phaser.Scene, o: T): T {
  let set = live.get(scene);
  if (!set) { set = new Set(); live.set(scene, set); }
  const s = set;
  s.add(o);
  o.once(Phaser.GameObjects.Events.DESTROY, () => s.delete(o));
  return o;
}

/** Destroys every one-shot of `scene` still up. */
export function clearOneShots(scene: Phaser.Scene): void {
  const set = live.get(scene);
  if (!set) return;
  for (const o of [...set]) o.destroy();
  set.clear();
}

/** Kills every tween of `scene` and the one-shots they would have removed. */
export function killTweens(scene: Phaser.Scene): void {
  scene.tweens.killAll();
  clearOneShots(scene);
}
